import { useCallback, useEffect, useRef, useState } from 'react'
import {
  parseHeartRateMeasurement,
  type BleDeviceData,
  type BleDeviceSnapshot,
  type BleHrStream,
} from '@versports/ble-contracts'
import { EMPTY_POLAR_LIVE, PolarLiveTracker, type PolarLive } from './sensorLive'

/**
 * Polar H10 conectado DIRECTO a este equipo por Web Bluetooth, desde el Device
 * Panel del admin. Mismo GATT (0x180D/0x2A37) y mismo parser que el Polar
 * directo del vr-app (`sensors/polarDirect.ts`), pero acá no hay sesión ni
 * bronze: es una vista de operador para verificar que el sensor emite, con
 * qué HR/RR y a qué cadencia, sin depender de una RPi. Pedido de mg,
 * 2026-09-07: "conectar local y ver los datos siendo recibidos", mostrado
 * con la misma card que los dispositivos de la RPi.
 */

/* Web Bluetooth no está en lib.dom sin @types/web-bluetooth; declaración
 * mínima local (misma que en vr-app). */
interface BtCharacteristic extends EventTarget {
  startNotifications(): Promise<BtCharacteristic>
  stopNotifications(): Promise<BtCharacteristic>
  value?: DataView
}
interface BtService {
  getCharacteristic(name: string): Promise<BtCharacteristic>
}
interface BtGattServer {
  connected: boolean
  connect(): Promise<BtGattServer>
  disconnect(): void
  getPrimaryService(name: string): Promise<BtService>
}
interface BtDevice extends EventTarget {
  id?: string
  name?: string
  gatt?: BtGattServer
}
interface BtApi {
  requestDevice(options: { filters: { services: string[] }[] }): Promise<BtDevice>
}

function bluetoothApi(): BtApi | null {
  const nav = navigator as Navigator & { bluetooth?: BtApi }
  return nav.bluetooth ?? null
}

/** El estado "En vivo" es el compartido con los sensores del kit (`sensorLive.ts`). */
export type PolarDirectLive = PolarLive

export interface PolarDirectState {
  supported: boolean
  connecting: boolean
  connected: boolean
  deviceName: string | null
  error: string | null
  live: PolarDirectLive
  /** Snapshot con la MISMA forma que un device de la RPi, para `BleDeviceCard`. */
  snapshot: BleDeviceSnapshot
  connect: () => Promise<void>
  disconnect: () => void
  /** Vacía trazas, ventana de RR y contadores; mantiene la conexión. */
  reset: () => void
}

const EMPTY_LIVE = EMPTY_POLAR_LIVE

export function usePolarDirect(log?: (message: string, type?: 'info' | 'success' | 'warning' | 'error') => void): PolarDirectState {
  const supported = bluetoothApi() !== null
  const [connecting, setConnecting] = useState(false)
  const [connected, setConnected] = useState(false)
  const [deviceName, setDeviceName] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [live, setLive] = useState<PolarDirectLive>(EMPTY_LIVE)

  const deviceRef = useRef<BtDevice | null>(null)
  const charRef = useRef<BtCharacteristic | null>(null)
  const trackerRef = useRef(new PolarLiveTracker())
  const logRef = useRef(log)
  logRef.current = log

  const teardown = useCallback(() => {
    const device = deviceRef.current
    if (device?.gatt?.connected) device.gatt.disconnect()
    deviceRef.current = null
    charRef.current = null
    trackerRef.current.reset()
  }, [])

  useEffect(() => () => teardown(), [teardown])

  const reset = useCallback(() => {
    setLive(trackerRef.current.reset())
    logRef.current?.('Polar directo: trazas, ventana de RR y contadores reiniciados.', 'info')
  }, [])

  const connect = useCallback(async () => {
    const bluetooth = bluetoothApi()
    if (!bluetooth) {
      setError('Web Bluetooth no disponible en este browser (usá Chrome o Edge).')
      return
    }
    if (connecting || connected) return
    setConnecting(true)
    setError(null)
    try {
      // requestDevice exige un gesto de usuario: se llama desde el click.
      const device = await bluetooth.requestDevice({ filters: [{ services: ['heart_rate'] }] })
      const server = await device.gatt!.connect()
      const service = await server.getPrimaryService('heart_rate')
      const characteristic = await service.getCharacteristic('heart_rate_measurement')
      deviceRef.current = device
      charRef.current = characteristic
      setLive(trackerRef.current.reset())

      characteristic.addEventListener('characteristicvaluechanged', (event) => {
        const view = (event.target as BtCharacteristic).value
        if (!view) return
        const { hr, rrMs } = parseHeartRateMeasurement(view)
        setLive(trackerRef.current.feed(hr, rrMs, Date.now()))
      })
      device.addEventListener('gattserverdisconnected', () => {
        logRef.current?.(`Polar directo: ${device.name ?? 'dispositivo'} se desconectó.`, 'warning')
        teardown()
        setConnected(false)
        setConnecting(false)
      })
      await characteristic.startNotifications()
      setConnected(true)
      setConnecting(false)
      setDeviceName(device.name ?? 'Polar')
      logRef.current?.(`Polar directo: conectado a ${device.name ?? 'Polar'} (heart_rate 0x2A37).`, 'success')
    } catch (err) {
      teardown()
      setConnecting(false)
      setConnected(false)
      setDeviceName(null)
      const message = err instanceof Error ? err.message : String(err)
      setError(message)
      logRef.current?.(`Polar directo: ${message}`, 'error')
    }
  }, [connecting, connected, teardown])

  const disconnect = useCallback(() => {
    teardown()
    setConnected(false)
    setConnecting(false)
    setDeviceName(null)
    setError(null)
    logRef.current?.('Polar directo: desconectado.', 'info')
  }, [teardown])

  let data: BleDeviceData | null = null
  if (connected && live.hr !== null) {
    const hr: BleHrStream = {
      heartrate: live.hr,
      rr_intervals: live.lastRrMs,
      updated_at: live.lastNotifyAt ? new Date(live.lastNotifyAt).toISOString() : undefined,
    }
    data = { hr }
  }
  const snapshot: BleDeviceSnapshot = {
    mac_address: deviceRef.current?.id ? `web-bluetooth:${deviceRef.current.id.slice(0, 12)}` : 'web-bluetooth (este equipo)',
    name: deviceName ?? 'Polar H10 (directo)',
    device_type: 'polar_h10',
    connected,
    status: connecting ? 'connecting' : connected ? 'connected' : 'disconnected',
    streams: ['hr'],
    active_streams: connected ? ['hr'] : [],
    updated_at: live.lastNotifyAt ? new Date(live.lastNotifyAt).toISOString() : null,
    last_error: error,
    data,
  }

  return { supported, connecting, connected, deviceName, error, live, snapshot, connect, disconnect, reset }
}
